import { TablePage, Dialog } from 'web-mojo';
import { RouteTemplate, RouteTemplateList } from '@portal/models/RouteTemplate.js';
import RouteTemplateEditorView from '@portal/routes/RouteTemplateEditorView.js';
import ScheduleRouteDrawer from '@portal/routes/views/ScheduleRouteDrawer.js';

RouteTemplate.VIEW_CLASS = RouteTemplateEditorView;

export default class RouteTemplateLibraryPage extends TablePage {
    constructor(options = {}) {
        super({
            title: 'Route Templates',
            icon: 'bi-signpost-split-fill',
            pageName: 'route_templates',
            route: 'group/routes/templates',
            Collection: RouteTemplateList,
            permissions: ['routes', 'view_routes', 'manage_routes', 'manage_group'],
            requiresGroup: true,

            viewDialogOptions: {
                header: false,
                size: 'xl',
                footer: false,
            },

            addButtonLabel: 'New Template',

            tableViewOptions: {
                hideActivePillNames: ['group'],
                onAdd: () => {
                    const editor = new RouteTemplateEditorView({ isNew: true });
                    const dialog = Dialog.showDialog({
                        header: false,
                        body: editor,
                        size: 'xl',
                        footer: false,
                    });
                    editor.on('saved', () => this.collection.fetch());
                    editor.on('activated', () => { dialog.hide(); this.collection.fetch(); });
                },
            },

            defaultQuery: {
                sort: 'name',
                graph: 'list',
                group: () => app.activeGroup?.id,
            },

            columns: [
                {
                    label: 'Name',
                    key: 'name',
                    formatter: "default('—')",
                },
                {
                    label: 'Type',
                    key: 'route_type',
                    formatter: "default('—')",
                    filter: { type: 'text' },
                    visibility: 'md',
                },
                {
                    label: 'Stops',
                    key: 'stop_count',
                    formatter: "default('0')",
                    visibility: 'md',
                },
                {
                    label: 'Recurrence',
                    key: 'recurrence_pattern',
                    formatter: "default('—')",
                    visibility: 'lg',
                },
                {
                    label: 'Status',
                    key: 'status',
                    formatter: "badge:draft=light,published=success,archived=secondary",
                    filter: {
                        type: 'select',
                        options: [
                            { label: 'Draft', value: 'draft' },
                            { label: 'Published', value: 'published' },
                            { label: 'Archived', value: 'archived' },
                        ]
                    }
                },
                {
                    label: 'Last Optimised',
                    key: 'last_optimized_at',
                    formatter: 'relative',
                    visibility: 'xl',
                },
            ],

            selectable: true,

            batchActions: [
                { label: 'Archive', icon: 'bi bi-archive', action: 'archive' },
                { label: 'Export', icon: 'bi bi-download', action: 'export' },
            ],
            searchable: true,
            sortable: true,
            filterable: true,
            paginated: true,

            showExport: false,

            tableOptions: {
                striped: true,
                bordered: false,
                hover: true,
                responsive: false,
            },

            contextMenu: [
                { icon: 'bi-pencil', label: 'Edit', action: 'edit-template' },
                { icon: 'bi-calendar-plus', label: 'Schedule', action: 'schedule-template' },
                { icon: 'bi-copy', label: 'Clone', action: 'clone-template' },
                { icon: 'bi-archive', label: 'Archive', action: 'archive-template', className: 'text-warning' },
            ],

            ...options,
        });
    }

    async onActionBatchArchive() {
        const selected = this.tableView.getSelectedItems();
        if (!selected?.length) return;
        const confirmed = await Dialog.confirm({
            title: `Archive ${selected.length} template${selected.length > 1 ? 's' : ''}`,
            message: 'Archive selected templates? They will no longer be available for scheduling.',
            confirmText: 'Archive',
            confirmClass: 'btn-warning',
        });
        if (!confirmed) return;
        await Promise.all(selected.map(item => item.model.save({ archive: 1 })));
        app.toast.success(`Archived ${selected.length} template${selected.length > 1 ? 's' : ''}`);
        this.tableView.collection.fetch();
    }

    async onActionBatchExport() {
        this.tableView.collection.export();
    }

    onActionEditTemplate(event, el) {
        const id = el.getAttribute('data-id');
        const template = this.collection.get(id);
        if (!template) return;
        const editor = new RouteTemplateEditorView({ model: template });
        const dialog = Dialog.showDialog({
            header: false,
            body: editor,
            size: 'xl',
            footer: false,
        });
        editor.on('saved', () => this.collection.fetch());
        editor.on('activated', () => { dialog.hide(); this.collection.fetch(); });
    }

    async onActionScheduleTemplate(event, el) {
        const id = el.getAttribute('data-id');
        const template = this.collection.get(id);
        if (!template) return;

        if (template.get('status') !== 'published') {
            app.toast.warning('Only published templates can be scheduled');
            return;
        }

        const drawer = new ScheduleRouteDrawer({ template });
        drawer.on('scheduled', () => app.toast.success('Route scheduled successfully'));
        Dialog.showDialog({
            title: `Schedule — ${template.get('name')}`,
            body: drawer,
            size: 'md',
            buttons: [
                { text: 'Cancel', class: 'btn-secondary', dismiss: true },
                { text: 'Schedule Route', class: 'btn-primary',
                  handler: () => drawer.onActionSchedule()
                },
            ],
        });
    }

    async onActionCloneTemplate(event, el) {
        const id = el.getAttribute('data-id');
        const template = this.collection.get(id);
        if (!template) return;

        const response = await app.api.post(`/api/routes/template/${id}`, { action: 'clone' });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to clone template');
            return;
        }
        app.toast.success('Template cloned');
        this.collection.fetch();
        // Open the cloned template in the editor
        const cloned = new RouteTemplate(response.data.data);
        const clonedEditor = new RouteTemplateEditorView({ model: cloned });
        const cloneDialog = Dialog.showDialog({
            header: false,
            body: clonedEditor,
            size: 'xl',
            footer: false,
        });
        clonedEditor.on('activated', () => { cloneDialog.hide(); this.collection.fetch(); });
    }

    async onActionArchiveTemplate(event, el) {
        const id = el.getAttribute('data-id');
        const template = this.collection.get(id);
        if (!template) return;

        if (template.get('status') !== 'published') {
            app.toast.warning('Only published templates can be archived');
            return;
        }

        const confirmed = await Dialog.confirm({
            title: 'Archive Template',
            message: `Archive "${template.get('name')}"? It will no longer be available for scheduling.`,
            confirmText: 'Archive',
            confirmClass: 'btn-warning',
        });
        if (!confirmed) return;

        const response = await app.api.post(`/api/routes/template/${id}`, { action: 'archive' });
        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to archive template');
            return;
        }
        app.toast.success('Template archived');
        this.collection.fetch();
    }
}
